// GLOBAL-NAV-1 — page registry. The single place a page declares how it appears
// in navigation. FUTURE PAGES: add one entry here (title, parent for breadcrumbs,
// entityType + breadcrumbLabel for entity views, pin/recent eligibility) and the
// nav bar, breadcrumbs, recents and pins all pick it up — no nav code per page.
// See plans/GLOBAL_NAV_ARCHITECTURE.md.

export const NAV_REGISTRY = {
  dashboard:      { title: "Home",         parent: null,        recentEligible: false },
  planner:        { title: "Planner",      parent: null },
  claims:         { title: "Workbench",    parent: null,        pinEligible: true },
  "claim-detail": { title: "Claim",        parent: "claims",    entityType: "claim",
                    pinEligible: true, recentEligible: true,
                    label: (loc) => loc.params?.claimLabel || `Claim ${loc.entityId}` },
  directory:      { title: "Directory",    parent: null },
  companies:      { title: "Companies",    parent: "directory", entityType: "company",
                    label: (loc) => loc.params?.companyLabel || "Companies" },
  contacts:       { title: "Contacts",     parent: "directory", entityType: "contact",
                    label: (loc) => loc.params?.contactLabel || "Contacts" },
  reports:        { title: "Reports",      parent: null,        pinEligible: true,
                    label: (loc) => loc.params?.label || "Reports" },
  operations:     { title: "Operations",   parent: null,        pinEligible: true },
  invoice:        { title: "Invoice",       parent: "operations", entityType: "invoice",
                    pinEligible: true, recentEligible: true,
                    label: (loc) => loc.params?.label || `Invoice #${loc.entityId}` },
  analytics:      { title: "Analytics",    parent: null },
  policies:       { title: "Company Policies", parent: null },
  mynumbers:      { title: "My Numbers",   parent: null },
  profile:        { title: "Profile",      parent: null },
  admin:          { title: "Admin",        parent: null },
  sysadmin:       { title: "System Admin", parent: null },
};

// Display label for a location (breadcrumb crumb / recent / pin / tab title).
// Prefers a label the page reported via reportLabel() (e.g. the real claim #).
export function navLabel(loc) {
  if (loc.params?.label) return loc.params.label;
  const r = NAV_REGISTRY[loc.page];
  if (!r) return loc.page;
  return typeof r.label === "function" ? r.label(loc) : r.title;
}

// Breadcrumb chain: ancestors (by parent) → current. Returns [{loc, label, isCurrent}].
// If the current location carries a `params.subLabel` (a singleton page reporting its
// active sub-view, e.g. Reports → "WIP"), the page becomes a clickable crumb and the
// sub-view is the current crumb → "Reports › WIP".
export function breadcrumbChain(current) {
  const crumbs = [];
  const reg = NAV_REGISTRY[current.page] || {};
  let parentId = reg.parent;
  while (parentId) {
    const pr = NAV_REGISTRY[parentId];
    if (!pr) break;
    crumbs.unshift({ loc: { page: parentId, params: {} }, label: pr.title, isCurrent: false });
    parentId = pr.parent;
  }
  const sub = current.params?.subLabel;
  if (sub) {
    crumbs.push({ loc: { page: current.page, params: {} }, label: navLabel({ page: current.page, params: {} }), isCurrent: false });
    crumbs.push({ loc: current, label: sub, isCurrent: true });
  } else {
    crumbs.push({ loc: current, label: navLabel(current), isCurrent: true });
  }
  return crumbs;
}

export function isEntityLoc(loc) {
  return loc && loc.entityId != null;
}

// Reconstruct a nav location from a stored pin/recent row. SINGLE source —
// imported by GlobalNavBar AND JumpBackIn (they previously each had their own
// copy, which drifted: REPORTS-UX-1's report-view special-case landed in one
// and not the other, spawning a duplicate keep-alive instance). Keep it here.
//
// REPORTS-UX-1: report views ride entirely in params (report + view), NOT as
// entity locations — so they resolve back to the single "reports" page/keep-
// alive instance and the bridge re-applies the exact view state on arrival.
export function locFromPref(r) {
  if (r.kind === "report_view" || r.kind === "report") {
    // subLabel drives the "Reports › <view>" breadcrumb crumb immediately on
    // restore (the bridge would otherwise only set it on the next view edit).
    return { page: "reports", params: { ...(r.params || {}), label: r.label, subLabel: r.label } };
  }
  const isEntity = r.kind !== "page";
  return { page: r.page, entityType: isEntity ? r.kind : undefined,
           entityId: isEntity ? r.ref_id : undefined, params: { ...(r.params || {}), label: r.label } };
}
